import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Banknote, Clock4, Lock, RefreshCw, ShieldCheck, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = ['PENDING', 'PARTIAL', 'SETTLED', 'FAILED', 'REVERSED'];

const stateStyle = {
  PENDING:  'bg-amber-50 text-amber-700 border-amber-200',
  PARTIAL:  'bg-blue-50 text-blue-700 border-blue-200',
  SETTLED:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  FAILED:   'bg-rose-50 text-rose-700 border-rose-200',
  REVERSED: 'bg-slate-100 text-slate-700 border-slate-300',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const fmtNum = (s) => {
  const n = Number(s);
  if (Number.isNaN(n)) return s;
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const agingDays = (iso) => {
  if (!iso) return 0;
  const diff = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
};
const agingClass = (days, terminal) => {
  if (terminal) return 'text-slate-400';
  if (days >= 7) return 'text-rose-600 font-semibold';
  if (days >= 3) return 'text-amber-600 font-semibold';
  return 'text-slate-600';
};

const FinanceSettlementsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState('all');
  const [counterparty, setCounterparty] = useState('');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);

  const fetchSettlements = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (stateFilter !== 'all') params.state = stateFilter;
      if (counterparty) params.counterparty_id = counterparty;
      const r = await axios.get(`${API}/finance/settlements`, { headers: getAuthHeaders(), params });
      setSettlements(r.data.settlements || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSettlements(); /* eslint-disable-next-line */ }, [stateFilter]);

  const stateCounts = useMemo(() => {
    const c = { PENDING: 0, PARTIAL: 0, SETTLED: 0, FAILED: 0, REVERSED: 0 };
    settlements.forEach((s) => { c[s.state] = (c[s.state] || 0) + 1; });
    return c;
  }, [settlements]);

  const byProvider = useMemo(() => {
    const g = {};
    settlements.forEach((s) => {
      if (s.counterparty_type !== 'provider') return;
      const k = s.counterparty_id;
      if (!g[k]) g[k] = { count: 0, expected: 0, settled: 0 };
      g[k].count += 1;
      g[k].expected += Number(s.expected_amount || 0);
      g[k].settled += Number(s.settled_amount || 0);
    });
    return Object.entries(g).sort((a, b) => b[1].count - a[1].count).slice(0, 5);
  }, [settlements]);

  const filtered = useMemo(() => {
    return settlements.filter((s) => {
      if (counterparty && !(s.counterparty_id || '').toLowerCase().includes(counterparty.toLowerCase())) return false;
      if (search) {
        const t = search.toLowerCase();
        if (!(
          (s.id || '').toLowerCase().includes(t) ||
          (s.counterparty_id || '').toLowerCase().includes(t) ||
          (s.counterparty_type || '').toLowerCase().includes(t)
        )) return false;
      }
      return true;
    });
  }, [settlements, search, counterparty]);

  return (
    <div className="space-y-6" data-testid="finance-settlements-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Banknote className="w-6 h-6 text-indigo-600" />
            Liquidaciones · Settlement Dashboard
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Estados de liquidación con counterparties (provider, customer, bank, exchange). Sin auto-fix.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700 gap-1">
            <Lock className="w-3 h-3" /> Read-only
          </Badge>
          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 gap-1">
            <ShieldCheck className="w-3 h-3" /> Sin automatización
          </Badge>
          <Button size="sm" variant="outline" onClick={fetchSettlements} data-testid="settlement-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
        </div>
      </div>

      {/* State summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {STATES.map((st) => (
          <Card
            key={st}
            className={`border-0 shadow-sm p-3 cursor-pointer transition ${stateFilter === st ? 'ring-2 ring-indigo-400' : ''}`}
            onClick={() => setStateFilter(stateFilter === st ? 'all' : st)}
            data-testid={`settlement-state-card-${st}`}
          >
            <div className="flex items-center justify-between">
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${stateStyle[st]}`}>{st}</span>
              <span className="text-xl font-bold text-slate-800">{stateCounts[st] || 0}</span>
            </div>
          </Card>
        ))}
      </div>

      {/* Provider grouping */}
      {byProvider.length > 0 && (
        <Card className="border-0 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-slate-500" />
            <h3 className="font-semibold text-slate-800 text-sm">Top providers por volumen de liquidaciones</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {byProvider.map(([pid, info]) => (
              <div key={pid} className="border rounded-lg p-3 bg-slate-50/50">
                <p className="font-mono text-xs text-slate-700 truncate" title={pid}>{pid}</p>
                <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                  <div>
                    <p className="text-slate-400">#</p>
                    <p className="font-semibold text-slate-800">{info.count}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Esperado</p>
                    <p className="font-mono text-blue-700">{fmtNum(info.expected)}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Liquidado</p>
                    <p className="font-mono text-emerald-700">{fmtNum(info.settled)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-0 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-500 font-medium">Estado</label>
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger data-testid="settlement-state-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Counterparty ID</label>
            <Input
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              placeholder="provider_… / customer_…"
              data-testid="settlement-counterparty-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Buscar</label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="id / tipo…"
              data-testid="settlement-search-input"
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Banknote className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p>Sin liquidaciones para mostrar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b text-left">
                  <th className="p-3 font-medium text-slate-600">Estado</th>
                  <th className="p-3 font-medium text-slate-600">Tipo</th>
                  <th className="p-3 font-medium text-slate-600">Counterparty</th>
                  <th className="p-3 font-medium text-slate-600">Moneda</th>
                  <th className="p-3 font-medium text-slate-600 text-right">Esperado</th>
                  <th className="p-3 font-medium text-slate-600 text-right">Liquidado</th>
                  <th className="p-3 font-medium text-slate-600">TXs</th>
                  <th className="p-3 font-medium text-slate-600">Aging</th>
                  <th className="p-3 font-medium text-slate-600">Creado</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const days = agingDays(s.created_at);
                  const terminal = s.state === 'SETTLED' || s.state === 'FAILED' || s.state === 'REVERSED';
                  return (
                    <tr
                      key={s.id}
                      className="border-b hover:bg-slate-50 cursor-pointer"
                      onClick={() => setDetail(s)}
                      data-testid={`settlement-row-${s.id}`}
                    >
                      <td className="p-3">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${stateStyle[s.state]}`}>{s.state}</span>
                      </td>
                      <td className="p-3 text-slate-700 text-xs">{s.counterparty_type}</td>
                      <td className="p-3 font-mono text-xs text-slate-700">{(s.counterparty_id || '').slice(0, 16)}…</td>
                      <td className="p-3 text-slate-600">{s.currency}</td>
                      <td className="p-3 text-right font-mono text-blue-700">{fmtNum(s.expected_amount)}</td>
                      <td className="p-3 text-right font-mono text-emerald-700">{fmtNum(s.settled_amount)}</td>
                      <td className="p-3 text-xs text-slate-500">{(s.tx_ids || []).length}</td>
                      <td className={`p-3 text-xs ${agingClass(days, terminal)}`}>
                        <span className="inline-flex items-center gap-1"><Clock4 className="w-3 h-3" />{days}d</span>
                      </td>
                      <td className="p-3 text-slate-400 whitespace-nowrap text-xs">{fmtDate(s.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="settlement-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-indigo-600" />
              Liquidación
              {detail && <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${stateStyle[detail.state]}`}>{detail.state}</span>}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-slate-500">ID</p><p className="font-mono text-xs break-all">{detail.id}</p></div>
                <div><p className="text-xs text-slate-500">Counterparty</p><p className="font-mono text-xs">{detail.counterparty_type} · {detail.counterparty_id}</p></div>
                <div><p className="text-xs text-slate-500">Esperado</p><p className="font-mono text-blue-700">{fmtNum(detail.expected_amount)} {detail.currency}</p></div>
                <div><p className="text-xs text-slate-500">Liquidado</p><p className="font-mono text-emerald-700">{fmtNum(detail.settled_amount)} {detail.currency}</p></div>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Transactions vinculadas ({(detail.tx_ids || []).length})</p>
                <div className="border rounded-lg p-2 bg-slate-50 max-h-32 overflow-y-auto">
                  {(detail.tx_ids || []).map((t) => <div key={t} className="font-mono text-xs text-slate-700">{t}</div>)}
                  {(detail.tx_ids || []).length === 0 && <span className="text-slate-400 text-xs">—</span>}
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Clock4 className="w-3 h-3" /> Línea de tiempo</p>
                <div className="border rounded-lg overflow-hidden">
                  {(detail.history || []).map((h, idx) => (
                    <div key={`${h.state}-${h.at ?? idx}`} className="px-3 py-2 border-b last:border-0 flex items-center justify-between bg-white">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${stateStyle[h.state] || ''}`}>{h.state}</span>
                        {h.note && <span className="text-xs text-slate-500">{h.note}</span>}
                      </div>
                      <span className="text-xs text-slate-400">{fmtDate(h.at)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-slate-400 italic flex items-center gap-1">
                <Lock className="w-3 h-3" /> Historial inmutable · No se permite edición manual.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceSettlementsPage;
